import React from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ImageGalleryDialog = ({
  imageViewProduct, setImageViewProduct,
  imageZoom, setImageZoom,
  galleryIndex, setGalleryIndex,
  imgBaseSize, setImgBaseSize,
  galleryImgRef,
  brands,
}) => {
  const getBrandName = (id) => brands.find(b => b.id === id)?.name || 'Unknown';

  return (
    <Dialog open={!!imageViewProduct} onOpenChange={v => { if (!v) { setImageViewProduct(null); setImageZoom(1); setGalleryIndex(0); setImgBaseSize({ w: 0, h: 0 }); } }}>
      <DialogContent className="max-w-4xl z-50 p-0 overflow-hidden" data-testid="image-gallery-dialog">
        <DialogHeader className="px-5 pt-4 pb-0">
          <DialogTitle className="text-lg flex items-center gap-2">
            {imageViewProduct?.design_code} — {imageViewProduct?.category}
            {imageViewProduct?.images?.length > 0 && (
              <span className="text-sm font-normal text-gray-400">
                {galleryIndex + 1} / {imageViewProduct.images.length}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {getBrandName(imageViewProduct?.brand_id)}{imageViewProduct?.fabric ? ` | ${imageViewProduct.fabric}` : ''}
            {imageViewProduct?.images?.length > 0 && imageViewProduct?.image_colors?.[imageViewProduct.images[galleryIndex]] && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                {imageViewProduct.image_colors[imageViewProduct.images[galleryIndex]]}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Main Image Area */}
        <div className="relative" style={{ height: '65vh', minHeight: '420px' }} data-testid="image-viewer-container">
          {imageViewProduct?.images?.length > 0 ? (
            <>
              {/* Scrollable image container */}
              <div className="absolute inset-0 overflow-auto bg-gray-950">
                <div className="flex items-center justify-center" style={{ minHeight: '100%', minWidth: '100%' }}>
                  <img
                    ref={galleryImgRef}
                    src={`${API_BASE}${imageViewProduct.images[galleryIndex]}`}
                    alt={`${imageViewProduct.design_code} - ${imageViewProduct.image_colors?.[imageViewProduct.images[galleryIndex]] || ''}`}
                    className="object-contain block"
                    onLoad={() => {
                      if (galleryImgRef.current && imageZoom === 1) {
                        setImgBaseSize({ w: galleryImgRef.current.clientWidth, h: galleryImgRef.current.clientHeight });
                      }
                    }}
                    style={imageZoom === 1
                      ? { maxWidth: '100%', maxHeight: '65vh', width: 'auto', height: 'auto' }
                      : { width: `${imgBaseSize.w * imageZoom}px`, height: `${imgBaseSize.h * imageZoom}px`, maxWidth: 'none', maxHeight: 'none' }
                    }
                    data-testid="image-viewer-img"
                  />
                </div>
              </div>
              {/* Prev Arrow */}
              {imageViewProduct.images.length > 1 && (
                <button
                  onClick={() => { setGalleryIndex(i => (i - 1 + imageViewProduct.images.length) % imageViewProduct.images.length); setImageZoom(1); setImgBaseSize({ w: 0, h: 0 }); }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 backdrop-blur flex items-center justify-center text-white transition-colors"
                  data-testid="gallery-prev-btn"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                </button>
              )}
              {/* Next Arrow */}
              {imageViewProduct.images.length > 1 && (
                <button
                  onClick={() => { setGalleryIndex(i => (i + 1) % imageViewProduct.images.length); setImageZoom(1); setImgBaseSize({ w: 0, h: 0 }); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 backdrop-blur flex items-center justify-center text-white transition-colors"
                  data-testid="gallery-next-btn"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                </button>
              )}
              {/* Color label overlay */}
              {imageViewProduct.image_colors?.[imageViewProduct.images[galleryIndex]] && (
                <div className="absolute top-3 left-3 z-10 bg-black/60 text-white text-sm px-3 py-1 rounded-full font-medium backdrop-blur" data-testid="gallery-color-label">
                  {imageViewProduct.image_colors[imageViewProduct.images[galleryIndex]]}
                </div>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-950">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto bg-white rounded-xl shadow-sm flex items-center justify-center mb-3">
                  <span className="text-3xl font-bold text-blue-600">{imageViewProduct?.design_code}</span>
                </div>
                <p className="text-gray-400">No images available</p>
              </div>
            </div>
          )}
        </div>

        {/* Thumbnail Strip */}
        {imageViewProduct?.images?.length > 1 && (
          <div className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 overflow-x-auto" data-testid="gallery-thumbnails">
            {imageViewProduct.images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => { setGalleryIndex(idx); setImageZoom(1); setImgBaseSize({ w: 0, h: 0 }); }}
                className={`relative w-14 h-14 rounded-lg overflow-hidden border-2 transition-all shrink-0 ${idx === galleryIndex ? 'border-blue-500 ring-2 ring-blue-200 scale-105' : 'border-gray-300 opacity-70 hover:opacity-100'}`}
                data-testid={`gallery-thumb-${idx}`}
              >
                <img src={`${API_BASE}${img}`} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
                {imageViewProduct.image_colors?.[img] && (
                  <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] text-center py-0.5 truncate">{imageViewProduct.image_colors[img]}</span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Zoom Controls */}
        <div className="flex items-center justify-center gap-3 px-4 pb-3">
          <Button variant="outline" size="sm" onClick={() => setImageZoom(z => Math.max(0.5, z - 0.25))} disabled={imageZoom <= 0.5} data-testid="image-zoom-out">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" /></svg>
          </Button>
          <span className="text-sm text-gray-500 min-w-[50px] text-center">{Math.round(imageZoom * 100)}%</span>
          <Button variant="outline" size="sm" onClick={() => setImageZoom(z => Math.min(3, z + 0.25))} disabled={imageZoom >= 3} data-testid="image-zoom-in">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setImageZoom(1)} data-testid="image-zoom-reset">Reset</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageGalleryDialog;
